// packages/app/src/scaffolder/AzureDevOpsRepositoryPicker/AzureDevOpsRepositoryPickerExtension.tsx

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { TextField, CircularProgress } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { Repository, RepositoriesResponse } from './types';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

/**
 * Componente para seleccionar repositorios de Azure DevOps basado en un proyecto
 */
export const AzureDevOpsRepositoryPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [open, setOpen] = useState(false);

  // Obtener configuración del uiSchema
  const limit = Number(uiSchema?.['ui:options']?.limit) || 50;
  
  // Obtener el nombre del proyecto desde formContext (valores dinámicos del formulario)
  const projectName = useMemo(() => 
    formContext?.formData?.azureDevOpsProject || 
    uiSchema?.['ui:options']?.projectName || '', 
    [formContext?.formData?.azureDevOpsProject, uiSchema?.['ui:options']?.projectName]
  );

  /**
   * Función para cargar repositorios desde el backend
   */
  const fetchRepositories = useCallback(
    async (pageNum: number = 1, searchQuery: string = '') => {
      if (!projectName) {
        setError('Proyecto requerido para cargar repositorios');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Obtener la URL base del backend usando DiscoveryApi
        const baseUrl = await discoveryApi.getBaseUrl('azuredevops-picker');
        
        const params = new URLSearchParams({
          page: pageNum.toString(),
          limit: limit.toString(),
        });

        if (searchQuery) {
          params.append('search', searchQuery);
        }

        const url = `${baseUrl}/projects/${encodeURIComponent(projectName)}/repositories?${params.toString()}`;
        
        // Usar FetchApi que maneja automáticamente la autenticación
        const response = await fetchApi.fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error(`Error al cargar repositorios: ${response.status} ${response.statusText}`);
        }

        const data: RepositoriesResponse = await response.json();
        
        // Filtrar repositorios deshabilitados
        const activeRepositories = data.repositories.filter(repo => !repo.isDisabled);
        
        // Si es la primera página, reemplaza; si no, agrega a la lista existente
        if (pageNum === 1) {
          setRepositories(activeRepositories);
        } else {
          setRepositories(prev => [...prev, ...activeRepositories]);
        }

        setHasMore(data.pagination.hasNext);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [discoveryApi, fetchApi, projectName, limit]
  );

  // Limpiar el valor seleccionado cuando cambia el proyecto
  useEffect(() => {
    if (formData) {
      onChange(undefined);
    }
  }, [projectName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar repositorios cuando cambia el proyecto
  useEffect(() => {
    if (projectName) {
      setRepositories([]);
      setPage(1);
      fetchRepositories(1);
    } else {
      setRepositories([]);
      setError('Selecciona un proyecto primero');
    }
  }, [projectName]); // Removido fetchRepositories de las dependencias

  // Limpiar inputValue cuando se cierra el dropdown sin selección
  useEffect(() => {
    if (!open && !formData) {
      setInputValue('');
    }
  }, [open, formData]);

  /**
   * Maneja el cambio en el campo de búsqueda
   */
  const handleInputChange = (
    _event: React.ChangeEvent<{}>, 
    value: string, 
    reason: string
  ) => {
    // Siempre actualizar el inputValue para mostrar lo que el usuario está escribiendo
    setInputValue(value);
    
    // Solo realizar búsqueda si es un cambio por input del usuario (no por selección)
    if (reason === 'input' && projectName) {
      setPage(1);
      
      // Realizar búsqueda
      if (value.length > 0) {
        fetchRepositories(1, value);
      } else {
        fetchRepositories(1);
      }
    }
  };

  /**
   * Maneja el scroll para cargar más repositorios (paginación infinita)
   */
  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    
    if (
      listboxNode.scrollHeight - position <= 1 &&
      hasMore &&
      !loading &&
      projectName
    ) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchRepositories(nextPage, inputValue);
    }
  };

  // Convertir repositories a opciones (array de strings)
  const options = repositories.map(repo => repo.name);

  return (
    <Autocomplete
        className={classes.root}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        freeSolo={false}
        clearOnBlur={false}
        selectOnFocus={true}
        value={formData || null}
        onChange={(_event, newValue) => {
          onChange(newValue || undefined);
        }}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={options}
        loading={loading}
        loadingText="Cargando repositorios..."
        noOptionsText={
          !projectName 
            ? 'Selecciona un proyecto primero'
            : error 
              ? `Error: ${error}` 
              : 'No se encontraron repositorios'
        }
        disabled={!projectName}
        getOptionLabel={(option) => option}
        renderInput={params => (
          <TextField
            {...params}
            label="Repositorio de Azure DevOps"
            required={required}
            error={rawErrors && rawErrors.length > 0}
            helperText={
              rawErrors && rawErrors.length > 0
                ? rawErrors.join(', ')
                : !projectName
                  ? 'Primero selecciona un proyecto'
                  : `Busca y selecciona un repositorio)`
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(option) => (
          <div>{option}</div>
        )}
        ListboxProps={{
          onScroll: handleScroll,
          style: { maxHeight: '250px' },
        }}
      />
  );
};

// La validación se exporta desde validation.ts
